import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, CircularProgress, TextField, Container,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Paper, Chip,
  Card, CardContent, CardActions, Grid, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import WorkflowCanvas from '../../components/workflow/WorkflowCanvas';
import StepPalette from '../../components/workflow/StepPalette';
import StepConfigPanel from '../../components/workflow/StepConfigPanel';
import EligibilityRuleEditor from '../../components/workflow/EligibilityRuleEditor';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

function categoryColor(cat) {
  const colors = { subscription: 'primary', billing: 'secondary', override: 'warning', bulk: 'info', custom: 'default' };
  return colors[cat] || 'default';
}

// ─── Listing View ─────────────────────────────────────────────────

function WorkflowListing() {
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { loadDefinitions(); }, []);

  async function loadDefinitions() {
    setLoading(true);
    try {
      const data = await billingApi.get('/api/admin/workflows/definitions');
      setDefinitions(data.definitions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Group by category
  const grouped = {};
  for (const d of definitions) {
    const cat = d.category || 'custom';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  }

  const categoryLabels = {
    subscription: 'Subscription',
    billing: 'Billing',
    override: 'Overrides',
    bulk: 'Bulk Operations',
    custom: 'Custom',
  };

  const categoryOrder = ['subscription', 'billing', 'override', 'bulk', 'custom'];

  if (loading) {
    return <AdminPageSkeleton />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Workflows</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => navigate('/admin/workflows/editor/new')}>
          New Workflow
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {categoryOrder.filter(cat => grouped[cat]).map(cat => (
        <Box key={cat} sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
            color: 'text.secondary', display: 'block', mb: 1.5
          }}>
            {categoryLabels[cat] || cat} ({grouped[cat].length})
          </Typography>

          <Grid container spacing={2}>
            {grouped[cat].map(def => {
              const stepCount = (def.steps || []).length;
              return (
                <Grid item xs={12} sm={6} md={4} key={def.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {def.name}
                        </Typography>
                        {def.is_system ? (
                          <Chip label="System" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                        ) : null}
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1, minHeight: 36 }}>
                        {def.description || 'No description'}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={`v${def.version}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip label={`${stepCount} steps`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip label={def.trigger_type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        {def.customer_visible && (
                          <Chip label="Customer" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                      <Button size="small" startIcon={<EditIcon fontSize="inherit" />}
                        onClick={() => navigate(`/admin/workflows/editor/${def.workflowId}`)}>
                        Edit
                      </Button>
                      {def.trigger_type === 'manual' && (
                        <Button size="small" startIcon={<PlayArrowIcon fontSize="inherit" />}
                          onClick={() => navigate(`/admin/workflows/run/${def.workflowId}`)}>
                          Run
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {definitions.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No workflow definitions found</Typography>
        </Paper>
      )}
    </Container>
  );
}

// ─── Editor View ──────────────────────────────────────────────────

function WorkflowEditorCanvas({ workflowId: initialWorkflowId }) {
  const navigate = useNavigate();
  const workflowId = initialWorkflowId === 'new' ? null : initialWorkflowId;

  const [definition, setDefinition] = useState(null);
  const [steps, setSteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [loading, setLoading] = useState(!!workflowId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const [name, setName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [undoWindowHours, setUndoWindowHours] = useState(24);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    if (workflowId) loadDefinition(workflowId);
  }, [workflowId]);

  async function loadDefinition(s) {
    setLoading(true);
    try {
      const data = await billingApi.get(`/api/admin/workflows/definitions/${s}`);
      const def = data.definition;
      setDefinition(def);
      setName(def.name);
      setPublicName(def.public_name || '');
      setDescription(def.description || '');
      setCategory(def.category || 'custom');
      setUndoWindowHours(def.undo_window_hours || 0);
      setSteps(def.steps || []);
      setEligibility(def.eligibility || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleAddStep(type) {
    const id = `${type}_${Date.now().toString(36)}`;
    const newStep = { id, type, label: `New ${type} step`, config: {}, next: [], position: { x: steps.length * 200 + 100, y: 100 } };
    const updated = [...steps];
    if (updated.length > 0) {
      const last = { ...updated[updated.length - 1] };
      if (!last.next || last.next.length === 0) { last.next = [id]; updated[updated.length - 1] = last; }
    }
    updated.push(newStep);
    setSteps(updated);
    setSelectedStepId(id);
  }

  function handleStepChange(updatedStep) {
    setSteps(prev => prev.map(s => s.id === updatedStep.id ? updatedStep : s));
  }

  function handleDeleteStep(stepId) {
    setSteps(prev => prev.filter(s => s.id !== stepId).map(s => ({ ...s, next: (s.next || []).filter(n => n !== stepId) })));
    if (selectedStepId === stepId) setSelectedStepId(null);
  }

  async function handleSave() {
    if (!name.trim()) { setError('Workflow name is required'); return; }
    if (steps.length === 0) { setError('Workflow must have at least one step'); return; }
    setSaving(true);
    setError('');
    try {
      if (definition?.id) {
        await billingApi.put(`/api/admin/workflows/definitions/${definition.id}`, { name, public_name: publicName, description, category, undo_window_hours: undoWindowHours, steps, eligibility });
        setSnackbar('Workflow saved (new version created)');
      } else {
        const workflowIdValue = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const data = await billingApi.post('/api/admin/workflows/definitions', { workflowId: workflowIdValue, name, public_name: publicName, description, category, undo_window_hours: undoWindowHours, steps, eligibility });
        setDefinition(data.definition);
        setSnackbar('Workflow created');
        navigate(`/admin/workflows/editor/${workflowIdValue}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const selectedStep = steps.find(s => s.id === selectedStepId);

  if (loading) {
    return <AdminPageSkeleton />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/workflows/editor')}>
          All Workflows
        </Button>
        <TextField value={name} onChange={e => setName(e.target.value)} placeholder="Workflow name" size="small" sx={{ width: 250 }} />
        <TextField value={publicName} onChange={e => setPublicName(e.target.value)} placeholder="Public name (optional)" size="small" sx={{ width: 200 }}
          helperText={publicName ? '' : 'Shown to customers'} FormHelperTextProps={{ sx: { mt: 0, fontSize: '0.6rem' } }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={e => setCategory(e.target.value)}>
            <MenuItem value="subscription">Subscription</MenuItem>
            <MenuItem value="billing">Billing</MenuItem>
            <MenuItem value="override">Override</MenuItem>
            <MenuItem value="bulk">Bulk</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
        <TextField label="Undo window (hrs)" type="number" value={undoWindowHours}
          onChange={e => setUndoWindowHours(parseInt(e.target.value) || 0)} size="small" sx={{ width: 130 }} />
        <Box sx={{ flex: 1 }} />
        {definition?.is_system && (
          <Typography variant="caption" color="warning.main" sx={{ mr: 1 }}>System workflow — edits create new version</Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {steps.length} step{steps.length !== 1 ? 's' : ''}{definition ? ` · v${definition.version}` : ''}
        </Typography>
        <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}>Save</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <EligibilityRuleEditor eligibility={eligibility} onChange={setEligibility} />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ borderRight: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
          <StepPalette onAddStep={handleAddStep} />
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          {steps.length === 0 ? (
            <Paper variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>Add steps from the palette to build your workflow</Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddStep('validate')}>Add first step</Button>
              </Box>
            </Paper>
          ) : (
            <WorkflowCanvas steps={steps} selectedStepId={selectedStepId} onSelectStep={setSelectedStepId} />
          )}
        </Box>
        <StepConfigPanel step={selectedStep} allSteps={steps} onChange={handleStepChange}
          onDelete={handleDeleteStep} onClose={() => setSelectedStepId(null)} />
      </Box>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
}

// ─── Router Switch ────────────────────────────────────────────────

export default function WorkflowEditor() {
  const { workflowId } = useParams();

  // No workflowId → show listing. With workflowId → show editor.
  if (!workflowId) return <WorkflowListing />;
  return <WorkflowEditorCanvas workflowId={workflowId} />;
}
